import { PropsWithChildren, useCallback, useEffect, useMemo } from "react";
import { Panel, Separator } from "react-resizable-panels";
import { z } from "zod";

import { Button } from "@fronterahealth/frontera-ui-components";

import {
  LearnerFileType,
  useGetLearnerFileForReviewQuery,
  useUpdateLearnerFileParsedResultMutation,
} from "@api/graphql/types-and-hooks";
import { AssessmentFormRenderer, useAssessmentForm } from "@components/AssessmentFormRenderer";
import { ASSESSMENT_CONFIGS } from "@components/AssessmentFormRenderer/layouts";
import { Loader } from "@components/ClinicalNewReportDialog/Loader";
import { EffectiveDateEditor } from "@components/DocumentReviewModal/components/EffectiveDateEditor";
import { parseFormState } from "@components/DocumentReviewModal/components/utils";
import { notifySuccess } from "@components/notifications/notifications";

interface DocumentEditorProps {
  file: LearnerFileType;
  onDirtyChange?: (isDirty: boolean) => void;
}

type ParsedDataType = Record<string, unknown>;

const FALLBACK_SCHEMA = z.record(z.string(), z.unknown());

const PanelWrapper: React.FC<PropsWithChildren> = ({ children }) => {
  return (
    <>
      <Separator className="w-1 bg-limestone-200 hover:bg-limestone-400 transition-colors" />
      <Panel defaultSize={540} minSize={320} id="edit">
        {children}
      </Panel>
    </>
  );
};

export const DocumentEditor: React.FC<DocumentEditorProps> = ({ file, onDirtyChange }) => {
  const { data, isLoading, refetch } = useGetLearnerFileForReviewQuery(
    {
      fileId: file.id,
    },
    {
      gcTime: 0,
    },
  );

  const { mutateAsync: updateParsedResult } = useUpdateLearnerFileParsedResultMutation();

  const hasSchema = data?.learnerFileById?.hasParsedResultSchema;
  const config = ASSESSMENT_CONFIGS[file.fileKind];
  // Prefer this file's own scoped slice so multi-file reports show the file you
  // opened, not a merged sibling. Fall back to parsedResult for files parsed
  // before scoping existed (not yet re-parsed).
  const parsedResultString = data?.learnerFileById?.parsedResultScoped ?? data?.learnerFileById?.parsedResult;
  // When the panel is showing this file's own scoped slice, edits must save
  // back to the scoped field — writing them through parsedData would overwrite
  // the merged parsed_result used for report generation with a single file.
  const usedScoped = data?.learnerFileById?.parsedResultScoped != null;
  const savedEffectiveDate: string = data?.learnerFileById?.effectiveDate ?? "";
  const clinicianEditedAt: string | null = data?.learnerFileById?.clinicianEditedAt ?? null;

  const { parsedData, wrapperKey, values, errors } = useMemo(
    () => parseFormState(parsedResultString, config, file.fileKind),
    [parsedResultString, config, file.fileKind],
  );

  const handleSave = async (formData: ParsedDataType) => {
    const payload = wrapperKey ? { ...parsedData, [wrapperKey]: formData } : formData;
    const serialized = JSON.stringify(payload);
    await updateParsedResult({
      fileId: file.id,
      ...(usedScoped ? { parsedDataScoped: serialized } : { parsedData: serialized }),
      triggerSectionRegen: false,
    });
    notifySuccess("Successfully updated file data");
  };

  const schema = config?.parsedResultSchema ?? FALLBACK_SCHEMA;
  const { methods, submit, isDirty, isSubmitting, error } = useAssessmentForm({
    schema: schema as z.ZodSchema<ParsedDataType, ParsedDataType>,
    values: values ?? ({} as ParsedDataType),
    onSave: handleSave,
  });

  useEffect(() => {
    onDirtyChange?.(isDirty);
  }, [isDirty, onDirtyChange]);

  const handleEffectiveDateSaved = useCallback(async () => {
    await refetch();
  }, [refetch]);

  if (isLoading) {
    return (
      <PanelWrapper>
        <div className="flex items-center justify-center h-full">
          <Loader className="w-4 h-4 animate-spin" />
          <p>Loading...</p>
        </div>
      </PanelWrapper>
    );
  }

  const showSchemaForm = !!hasSchema && !!config && !!parsedResultString;

  return (
    <PanelWrapper>
      <div className="flex flex-col h-full">
        <div className="flex-1 overflow-y-auto p-6 flex flex-col gap-6">
          <EffectiveDateEditor
            fileId={file.id}
            savedEffectiveDate={savedEffectiveDate}
            clinicianEditedAt={clinicianEditedAt}
            onSaved={handleEffectiveDateSaved}
          />

          {showSchemaForm && values && (
            <AssessmentFormRenderer layout={config} methods={methods} onSubmit={submit} error={error} />
          )}
          {showSchemaForm && !values && (
            <div>
              <p>Invalid default values provided</p>
              <div className="flex flex-col">
                {errors.map((issue) => (
                  <div key={issue.path.join(".")} className="text-red-500">
                    {issue.path.join(".")}: {issue.message}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
        {showSchemaForm && values && !error && (
          <div className="border-t border-limestone-200 bg-white p-4 flex justify-end">
            <Button
              appearance="primary"
              onClick={submit}
              disabled={!isDirty || isSubmitting}
              text={isSubmitting ? "Saving..." : "Save Changes"}
            />
          </div>
        )}
      </div>
    </PanelWrapper>
  );
};
